import { createContext, useCallback, useContext, useMemo, useState, ReactNode } from 'react';
import useUserDetails from '../../hooks/useUserDetails';
import useErrorModal from '../../hooks/useErrorModal';
import { postChat } from '../_shared/chatService';
import { buildChatRequest, ScreenContextSlice } from './buildChatRequest';
import {
  INITIAL_CHAT_HISTORIES_BY_SECTION,
  INITIAL_DRAFTS,
  INITIAL_SECTION_STATUSES,
  SECTIONS,
  SectionStatus,
  parseChatResponse,
} from './prototype1Data';

type ScreenId =
  | '01-dashboard'
  | '02-college-eligibility-not-started'
  | '03-equity-gaps-drafting'
  | '04-chat-panel-closed'
  | '05-dashboard-report-overview'
  | '06-section-editor-ai-draft'
  | '07-dashboard-all-sections-complete'
  | '08-full-report-review';

interface ChatMessage {
  role: 'user' | 'assistant';
  content: string;
  synthetic?: boolean;
}

interface Prototype1ContextValue {
  currentScreen: ScreenId;
  activeSectionId: string;
  setActiveSectionId: (id: string) => void;
  goToScreen: (id: ScreenId) => void;
  chatHistoriesBySection: Record<string, ChatMessage[]>;
  isChatPending: boolean;
  sendChat: (message: string, slice: ScreenContextSlice) => Promise<void>;
  drafts: Record<string, string>;
  updateDraft: (sectionId: string, text: string) => void;
  undoDraft: (sectionId: string) => void;
  redoDraft: (sectionId: string) => void;
  canUndoDraft: (sectionId: string) => boolean;
  canRedoDraft: (sectionId: string) => boolean;
  sectionStatuses: Record<string, SectionStatus>;
  approveSection: (sectionId: string) => void;
  setSectionStatus: (sectionId: string, status: SectionStatus) => void;
  allSectionsComplete: boolean;
}

const MOCK_SCOPE_PREFIX = 'mock-';
const REPORT_CHAT_SCOPE = `${MOCK_SCOPE_PREFIX}report`;

const scopeOf = (slice: ScreenContextSlice): string =>
  slice.active_section_id ? `${MOCK_SCOPE_PREFIX}${slice.active_section_id}` : REPORT_CHAT_SCOPE;

// Local chat state is keyed by the raw section id (matching what the screens read and the
// seeded histories use); the mock-prefixed scope is only the backend thread identifier.
const localKeyOf = (slice: ScreenContextSlice): string => slice.active_section_id ?? REPORT_CHAT_SCOPE;

interface DraftEntry {
  text: string;
  past: string[];
  future: string[];
}

const DRAFT_HISTORY_LIMIT = 50;

const buildInitialDraftStore = (): Record<string, DraftEntry> =>
  Object.fromEntries(Object.entries(INITIAL_DRAFTS).map(([id, text]) => [id, { text, past: [], future: [] }]));

const pushDraft = (entry: DraftEntry, nextText: string): DraftEntry => {
  if (entry.text === nextText) return entry;
  const past = [...entry.past, entry.text];
  const trimmed = past.length > DRAFT_HISTORY_LIMIT ? past.slice(past.length - DRAFT_HISTORY_LIMIT) : past;
  return { text: nextText, past: trimmed, future: [] };
};

const baselineDraft = (entry: DraftEntry): DraftEntry => ({
  text: entry.text,
  past: [],
  future: [],
});

const Prototype1Context = createContext<Prototype1ContextValue | null>(null);

interface Prototype1ProviderProps {
  children: ReactNode;
}

const Prototype1Provider = ({ children }: Prototype1ProviderProps) => {
  const [currentScreen, setCurrentScreen] = useState<ScreenId>('01-dashboard');
  const [activeSectionId, setActiveSectionId] = useState('postsecondary-readiness');
  const [chatHistoriesBySection, setChatHistoriesBySection] = useState<Record<string, ChatMessage[]>>(
    INITIAL_CHAT_HISTORIES_BY_SECTION
  );
  const [conversationIdsBySection, setConversationIdsBySection] = useState<Record<string, string>>({});
  const [isChatPending, setIsChatPending] = useState(false);
  const [draftStore, setDraftStore] = useState<Record<string, DraftEntry>>(buildInitialDraftStore);
  const [sectionStatuses, setSectionStatuses] = useState<Record<string, SectionStatus>>(INITIAL_SECTION_STATUSES);

  const drafts = useMemo<Record<string, string>>(
    () => Object.fromEntries(Object.entries(draftStore).map(([id, entry]) => [id, entry.text])),
    [draftStore]
  );

  const {
    userDetails: { data: userDetails },
  } = useUserDetails();
  const { handleOpen: handleOpenErrorModal } = useErrorModal();

  const goToScreen = useCallback((id: ScreenId) => {
    setCurrentScreen(id);
  }, []);

  const updateDraft = useCallback((sectionId: string, text: string) => {
    setDraftStore((prev) => {
      const entry = prev[sectionId] ?? { text: '', past: [], future: [] };
      const next = pushDraft(entry, text);
      if (next === entry) return prev;
      return { ...prev, [sectionId]: next };
    });
  }, []);

  const undoDraft = useCallback((sectionId: string) => {
    setDraftStore((prev) => {
      const entry = prev[sectionId];
      if (!entry || entry.past.length === 0) return prev;
      const previous = entry.past[entry.past.length - 1];
      return {
        ...prev,
        [sectionId]: {
          text: previous,
          past: entry.past.slice(0, -1),
          future: [entry.text, ...entry.future],
        },
      };
    });
  }, []);

  const redoDraft = useCallback((sectionId: string) => {
    setDraftStore((prev) => {
      const entry = prev[sectionId];
      if (!entry || entry.future.length === 0) return prev;
      const [next, ...rest] = entry.future;
      return {
        ...prev,
        [sectionId]: {
          text: next,
          past: [...entry.past, entry.text],
          future: rest,
        },
      };
    });
  }, []);

  const canUndoDraft = useCallback((sectionId: string) => (draftStore[sectionId]?.past.length ?? 0) > 0, [draftStore]);

  const canRedoDraft = useCallback(
    (sectionId: string) => (draftStore[sectionId]?.future.length ?? 0) > 0,
    [draftStore]
  );

  const setSectionStatus = useCallback((sectionId: string, status: SectionStatus) => {
    setSectionStatuses((prev) => ({ ...prev, [sectionId]: status }));
  }, []);

  const approveSection = useCallback((sectionId: string) => {
    setSectionStatuses((prev) => ({ ...prev, [sectionId]: 'complete' }));
    setDraftStore((prev) => {
      const entry = prev[sectionId];
      if (!entry) return prev;
      return { ...prev, [sectionId]: baselineDraft(entry) };
    });
  }, []);

  const allSectionsComplete = useMemo(
    () => Object.values(sectionStatuses).every((s) => s === 'complete'),
    [sectionStatuses]
  );

  const sendChat = useCallback(
    async (message: string, slice: ScreenContextSlice) => {
      const trimmed = message.trim();
      if (!trimmed) return;

      const scope = scopeOf(slice);
      const localKey = localKeyOf(slice);
      const targetSectionId = slice.active_section_id;
      const priorHistory = chatHistoriesBySection[localKey] ?? [];
      const priorConversationId = conversationIdsBySection[localKey];

      const appendToScope = (msg: ChatMessage) =>
        setChatHistoriesBySection((prev) => ({
          ...prev,
          [localKey]: [...(prev[localKey] ?? []), msg],
        }));

      appendToScope({ role: 'user', content: trimmed });
      setIsChatPending(true);

      try {
        const eoaId = userDetails?.eoaId;
        const accessToken = userDetails?.access_token ?? '';
        if (!eoaId || !accessToken) {
          throw new Error('Missing EOA or access token');
        }

        const payload = buildChatRequest({
          message: trimmed,
          screenId: currentScreen,
          slice,
          conversation: priorHistory,
          conversationId: priorConversationId,
          scope,
          sectionStatuses,
          eoaId,
        });
        const { conversation_id, response } = await postChat(payload, accessToken);
        setConversationIdsBySection((prev) => ({ ...prev, [localKey]: conversation_id }));

        const parsed = parseChatResponse(response);
        const isKnownSection = (id?: string): id is string => Boolean(id) && SECTIONS.some((s) => s.id === id);

        const approveTarget = parsed.approve
          ? isKnownSection(parsed.approve.sectionId)
            ? parsed.approve.sectionId
            : targetSectionId
          : undefined;
        if (approveTarget) {
          approveSection(approveTarget);
        }

        const draftTarget =
          parsed.draft && (isKnownSection(parsed.draft.sectionId) ? parsed.draft.sectionId : targetSectionId);
        const draftBody = draftTarget ? parsed.draft?.body : undefined;
        if (draftTarget && draftBody) {
          setDraftStore((prev) => {
            const entry = prev[draftTarget] ?? { text: '', past: [], future: [] };
            const next = pushDraft(entry, draftBody);
            if (next === entry) return prev;
            return { ...prev, [draftTarget]: next };
          });
        }

        const chatText = parsed.chat ? parsed.chat : draftBody ? '✓ Draft updated. See the editor on the left.' : '';
        if (chatText) {
          appendToScope({ role: 'assistant', content: chatText });
        }
      } catch (error) {
        handleOpenErrorModal('Prototype chat request failed', error as Error);
        appendToScope({
          role: 'assistant',
          content: '(chat backend unavailable — this is a prototype)',
          synthetic: true,
        });
      } finally {
        setIsChatPending(false);
      }
    },
    [
      chatHistoriesBySection,
      conversationIdsBySection,
      currentScreen,
      sectionStatuses,
      userDetails,
      handleOpenErrorModal,
      approveSection,
    ]
  );

  const value = useMemo<Prototype1ContextValue>(
    () => ({
      currentScreen,
      activeSectionId,
      setActiveSectionId,
      goToScreen,
      chatHistoriesBySection,
      isChatPending,
      sendChat,
      drafts,
      updateDraft,
      undoDraft,
      redoDraft,
      canUndoDraft,
      canRedoDraft,
      sectionStatuses,
      approveSection,
      setSectionStatus,
      allSectionsComplete,
    }),
    [
      currentScreen,
      activeSectionId,
      goToScreen,
      chatHistoriesBySection,
      isChatPending,
      sendChat,
      drafts,
      updateDraft,
      undoDraft,
      redoDraft,
      canUndoDraft,
      canRedoDraft,
      sectionStatuses,
      approveSection,
      setSectionStatus,
      allSectionsComplete,
    ]
  );

  return <Prototype1Context.Provider value={value}>{children}</Prototype1Context.Provider>;
};

const usePrototype1 = () => {
  const ctx = useContext(Prototype1Context);
  if (!ctx) throw new Error('usePrototype1 must be used inside Prototype1Provider');
  return ctx;
};

export { Prototype1Provider, usePrototype1, REPORT_CHAT_SCOPE };
export type { ScreenId, ChatMessage };
